import PlacesAutoCompleteInput, {
  extractPlaceCountry,
  extractPlaceLocation,
} from "src/components/CustomSelect/PlacesAutoComplete";
import {
  CONTINENTS,
  JobLocationValue,
  REGIONS,
  REMOTE_SCOPES,
  RemoteScope,
  WORK_MODES,
  WorkMode,
  cityLabel,
  isOnsiteMode,
} from "./jobLocation";

type Props = {
  value: JobLocationValue;
  onChange: (next: JobLocationValue) => void;
  /** Validation message to show under the block, if any. */
  error?: string | null;
  /** Prefix for element ids so two instances can coexist. */
  idPrefix?: string;
  /**
   * Which halves to render. The current hire form shows both together; the
   * post-a-role wizard puts the arrangement on step 8 and the place on step 9.
   */
  show?: "all" | "arrangement" | "place";
};

const selectClass = (filled: boolean) =>
  `py-2 px-1 text-sm border-0 border-b-2 w-full bg-transparent ${filled ? "text-gray-700" : "text-muted-foreground"}`;

/**
 * Work arrangement + the location matching needs for it.
 *
 * Controlled: holds no state of its own, so the parent form decides where
 * the value lives (react-hook-form, a store, a reducer). Switching mode or
 * scope clears the fields the new choice does not use, so a stale city can't
 * ride along with a remote job.
 */
const JobLocationFields = ({ value, onChange, error, idPrefix = "job-location", show = "all" }: Props) => {
  const setMode = (workMode: WorkMode) =>
    onChange({ workMode, remoteScope: null, city: null, country: null, continent: null, region: null });

  const setScope = (remoteScope: RemoteScope) =>
    onChange({ ...value, remoteScope, country: null, continent: null, region: null });

  const onsite = isOnsiteMode(value.workMode);
  const remote = value.workMode === "remote";
  const scope = REMOTE_SCOPES.find((s) => s.id === value.remoteScope);

  return (
    <div className="flex flex-col gap-4">
      {show !== "place" && (
        <div className="flex flex-col gap-2">
          <p className="text-[#333333] text-sm">How will the talent work?</p>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Work arrangement">
            {WORK_MODES.map((m) => {
              const selected = value.workMode === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  title={m.description}
                  onClick={() => setMode(m.id)}
                  className={`rounded-full border px-4 py-1.5 text-sm transition ${
                    selected
                      ? "border-brand-orange bg-orange-50 text-brand-orange font-semibold"
                      : "border-gray-300 text-gray-600 hover:border-gray-400"
                  }`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
          {value.workMode && (
            <p className="text-xs text-muted-foreground italic">
              {WORK_MODES.find((m) => m.id === value.workMode)?.description}
            </p>
          )}
        </div>
      )}

      {show !== "arrangement" && onsite && (
        <div className="flex flex-col gap-2">
          <label htmlFor={`${idPrefix}-city`} className="text-[#333333] text-sm">
            Where is the job located?
          </label>
          <span className="w-full rounded-lg border p-2">
            <PlacesAutoCompleteInput
              id={`${idPrefix}-city`}
              kind="city"
              placeholder="Type a city, e.g. Port Harcourt"
              defaultText={cityLabel(value.city)}
              selectHandler={(place) => {
                const picked = extractPlaceLocation(place);
                onChange({
                  ...value,
                  city: picked
                    ? { city: picked.city, state: picked.state, country: picked.country, country_code: picked.country_code }
                    : null,
                });
              }}
              onClear={() => onChange({ ...value, city: null })}
            />
          </span>
          <p className="text-xs text-muted-foreground italic">
            Only talents in this city are matched. Pick it from the suggestions.
          </p>
        </div>
      )}

      {show !== "arrangement" && remote && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <p className="text-[#333333] text-sm">Remote scope</p>
            <p className="text-xs text-muted-foreground">
              This limits the search to candidates inside the scope you pick. Anyone outside it won't be matched to this role.
            </p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Remote scope">
              {REMOTE_SCOPES.map((s) => {
                const selected = value.remoteScope === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setScope(s.id)}
                    className={`rounded-full border px-4 py-1.5 text-sm transition ${
                      selected
                        ? "border-brand-orange bg-orange-50 text-brand-orange font-semibold"
                        : "border-gray-300 text-gray-600 hover:border-gray-400"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {value.remoteScope === "country" && (
            <div className="flex flex-col gap-2">
              <label htmlFor={`${idPrefix}-country`} className="text-[#333333] text-sm">{scope?.prompt}</label>
              <span className="w-full rounded-lg border p-2">
                <PlacesAutoCompleteInput
                  id={`${idPrefix}-country`}
                  kind="country"
                  placeholder="Type a country, e.g. Nigeria"
                  defaultText={value.country?.country ?? ""}
                  selectHandler={(place) => onChange({ ...value, country: extractPlaceCountry(place) })}
                  onClear={() => onChange({ ...value, country: null })}
                />
              </span>
            </div>
          )}

          {value.remoteScope === "continent" && (
            <div className="flex flex-col gap-2">
              <label htmlFor={`${idPrefix}-continent`} className="text-[#333333] text-sm">{scope?.prompt}</label>
              <select
                id={`${idPrefix}-continent`}
                value={value.continent ?? ""}
                onChange={(e) => onChange({ ...value, continent: e.target.value || null })}
                className={selectClass(!!value.continent)}
              >
                <option value="">Select a continent</option>
                {CONTINENTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}

          {value.remoteScope === "region" && (
            <div className="flex flex-col gap-2">
              <label htmlFor={`${idPrefix}-region`} className="text-[#333333] text-sm">{scope?.prompt}</label>
              <select
                id={`${idPrefix}-region`}
                value={value.region ?? ""}
                onChange={(e) => onChange({ ...value, region: e.target.value || null })}
                className={selectClass(!!value.region)}
              >
                <option value="">Select a region</option>
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          )}

          {value.remoteScope === "global" && (
            <p className="text-xs text-muted-foreground italic">
              Anywhere in the world — candidates from every country can be matched.
            </p>
          )}
        </div>
      )}

      {error && <div className="text-red-700 text-sm">{error}</div>}
    </div>
  );
};

export default JobLocationFields;
